import styled from '@emotion/styled';
import {EventApi} from '@fullcalendar/core';
import moment from 'moment';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {mixinFlexboxColumn, mixinFlexboxRow} from 'webapp-lib/styling';

import {useTimeEntriesPerDay} from '../hooks/useTimeEntriesPerDay';
import {useTimer} from '../hooks/useTimer';
import {EventApiWithTimEntryProps} from '../timetracker.types';

import {Calendar} from './Calendar';
import {Summary} from './Summary';
import {CalendarAPIContext} from './TimetrackerButton';
import {TimetrackerWarnings} from './TimetrackerWarnings';
import {Timer} from './timer/Timer';

const POLLING_INTERVAL_MINUTES = 1;

export function Timetracker({isOpen}: {isOpen: boolean}): React.ReactElement {
  const [events, setEvents] = useState<EventApiWithTimEntryProps[]>([]);
  const [currentDay, setCurrentDay] = useState<Date>(new Date());
  const {calendarAPI} = useContext(CalendarAPIContext);

  const {
    loadDay,
    timeEntriesPerDay,
    ongoingTimeEntry,
    createTimeEntry,
    updateTimeEntry,
    deleteTimeEntry,
  } = useTimeEntriesPerDay(calendarAPI);
  const {timer} = useTimer(ongoingTimeEntry, {
    createTimeEntry,
    updateTimeEntry,
  });
  const handleEventsChanged = (updatedEvents: EventApi[]) => {
    setEvents(updatedEvents as EventApiWithTimEntryProps[]);
  };

  const handleDayChanged = useCallback((date: Date) => {
    setCurrentDay((prev) => (moment(prev).isSame(date, 'day') ? prev : date));
  }, []);

  // tracks a running drag/resize in the calendar so the poll does not tear
  // down the events mid-interaction; a ref so the poll effect keeps running
  const interactionActiveRef = useRef(false);
  const handleInteractionChanged = useCallback((active: boolean) => {
    interactionActiveRef.current = active;
  }, []);

  // (Re)load from the backend when the panel is opened, the visible day changes,
  // or once the calendar API becomes available (initial load)
  // and poll while panel is open
  const api = calendarAPI();
  useEffect(() => {
    if (!api || !isOpen) {
      return;
    }
    loadDay(currentDay);
    const interval = setInterval(() => {
      if (interactionActiveRef.current) {
        return;
      }
      loadDay(currentDay);
    }, POLLING_INTERVAL_MINUTES * 60_000);
    return () => clearInterval(interval);
  }, [api, isOpen, currentDay, loadDay]);

  const timerMemoized = useMemo(
    () => <Timer ongoingTimeEntry={ongoingTimeEntry} timer={timer} />,
    [ongoingTimeEntry, timer]
  );

  return (
    <>
      <WithBottomDrawer>
        {timerMemoized}
        <MainPanels>
          {/* TODO LEXIAN-8286: show sidebar */}
          {/* <StyledFavoriteColumn>*/}
          {/*  <TimetrackerSidebar events={events} timer={timer} />*/}
          {/* </StyledFavoriteColumn>*/}
          <StyledCalendarColumn>
            <Calendar
              createTimeEntry={createTimeEntry}
              deleteTimeEntry={deleteTimeEntry}
              handleEventsChanged={handleEventsChanged}
              initialEvents={events}
              timer={timer}
              updateTimeEntry={updateTimeEntry}
              onDayChanged={handleDayChanged}
              onInteractionChanged={handleInteractionChanged}
            />
            <Summary durations={timeEntriesPerDay?.durations} />
          </StyledCalendarColumn>
        </MainPanels>
        <TimetrackerWarnings timeEntriesPerDay={timeEntriesPerDay} />
      </WithBottomDrawer>
    </>
  );
}

const MainPanels = styled.div`
  ${mixinFlexboxRow};
  flex-grow: 1;
  min-height: 0;

  padding: var(--gutter-medium);
  gap: var(--gutter);
`;

const WithBottomDrawer = styled.div`
  ${mixinFlexboxColumn};
  justify-content: space-between;
  height: 100%;
`;

// TODO LEXIAN-8286: show sidebar
// const StyledFavoriteColumn = styled.div`
//   ${mixinFlexboxColumn};
//   max-width: 40%;
// `;

const StyledCalendarColumn = styled.div`
  ${mixinFlexboxColumn};
  flex-grow: 2;
`;
